import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Plus, Send, X } from "lucide-react";

/** Message with up to 3 tap-to-reply buttons (only inside the 24-hour window) */
export function ButtonsDialog({
  open,
  onOpenChange,
  sending,
  onSend,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  sending: boolean;
  onSend: (body: string, buttons: string[]) => void;
}) {
  const [body, setBody] = useState("");
  const [buttons, setButtons] = useState<string[]>(["Yes", "No"]);

  useEffect(() => {
    if (open) {
      setBody("");
      setButtons(["Yes", "No"]);
    }
  }, [open]);

  const clean = buttons.map((b) => b.trim()).filter(Boolean);
  const duplicate = new Set(clean.map((b) => b.toLowerCase())).size !== clean.length;
  const ready = body.trim() && clean.length >= 1 && !duplicate;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg md:max-w-xl">
        <DialogHeader>
          <DialogTitle>Message with reply buttons</DialogTitle>
          <DialogDescription>The buyer taps a button to answer. Their choice comes back as a reply in this chat.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Message</Label>
            <Textarea rows={3} value={body} onChange={(e) => setBody(e.target.value)} placeholder="Shall we confirm the order for 500 m?" maxLength={1024} />
          </div>
          <div className="space-y-2">
            <Label>Buttons (max 3, 20 characters each)</Label>
            {buttons.map((b, i) => (
              <div key={i} className="flex items-center gap-2">
                <Input value={b} maxLength={20} onChange={(e) => setButtons((list) => list.map((x, idx) => (idx === i ? e.target.value : x)))} />
                {buttons.length > 1 && (
                  <Button type="button" variant="ghost" size="sm" className="h-10 w-10 sm:h-11 sm:w-11 shrink-0 p-0" aria-label="Remove button" onClick={() => setButtons((list) => list.filter((_, idx) => idx !== i))}>
                    <X className="size-4" />
                  </Button>
                )}
              </div>
            ))}
            {buttons.length < 3 && (
              <Button type="button" variant="outline" size="sm" className="h-9 sm:h-10 gap-1.5 text-xs sm:text-sm" onClick={() => setButtons((list) => [...list, ""])}>
                <Plus className="size-4" /> Add button
              </Button>
            )}
            {duplicate && <p className="text-xs sm:text-sm text-destructive">Buttons must be different.</p>}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={!ready || sending} onClick={() => onSend(body.trim(), clean)} className="gap-1.5">
            {sending ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />}
            Send
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
